import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { GuestType, Price, Seat, SeatType } from "@/graphql/types";
import Button from "../common/button/button";
import { isMobile } from "@/lib/ts/device";
import SeatsDisplay from "../seat/seatsDisplay";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";

type Props = {
  totalGroupSize: number;
  onSeatSelected: (seat: Seat) => void
  onSubmit: () => void;
  refreshSeats: () => Promise<void>
  seats: Seat[]
  selectedSeatIndexes: number[]
  seatTypes: SeatType[]
  prices: Price[]
  guestTypes: GuestType[]
};

const SeatSelectArea = (props: Props) => {
  const snackBar = useSnackBar({})
  const [showPriceModal, setShowPriceModal] = useState(false)


  const onSeatClick = useCallback((seat: Seat | undefined) => {
    if(seat) {
      props.onSeatSelected(seat)
      return false
    }
    return false
  }, [props]);

  const colorRule = useCallback((seat: Seat) => {
    if(seat.reservationId) {
      return "gray"
    }
    const index = props.seats.findIndex(item => seat.row === item.row && item.column === seat.column)
    if(props.selectedSeatIndexes.includes(index)) {
      return "red"
    }
    return undefined
  }, [props.seats, props.selectedSeatIndexes])

  const lockRule = useCallback((seat: Seat) => {
    if(seat.locked || seat.reservationId || seat.type === -1) {
      return true
    }
    return false
  }, [])

  const disableSubmit = useMemo(() => {
    if(props.selectedSeatIndexes.length !== props.totalGroupSize) {
      return true
    }
    return false
  }, [props.selectedSeatIndexes.length, props.totalGroupSize])

  const selectedSeatsNames = useMemo(() => {
    return props.selectedSeatIndexes.map(index => props.seats[index].seatName)
  }, [props.seats, props.selectedSeatIndexes])

  const showPriceList = useMemo(() => {
    return props.prices.some(p => p.price > 0)
  }, [props.prices])

  const getPrice = useCallback((seatTypeId: number, guestTypeId: number) => {
    const price = props.prices.find(p => p.guestType === guestTypeId && p.seatType === seatTypeId)
    return price ? price.price : undefined
  }, [props.prices])

  const priceListModal = useMemo(() => {
    return (
      <PriceListModalStyle>
        <div className="modal-overlay" onClick={() => { setShowPriceModal(false) }}></div>
        <div className="modal-wrapper">
          <div className="modal">
            <p className="modal-title">価格一覧</p>
            {
              props.guestTypes.map(guestType => (
                <div className="price-seat-type">
                  <p className="price-seat-type-name">{guestType.name}</p>
                  <div>
                    {
                      props.seatTypes.map(seatType => (
                        <>{
                          getPrice(seatType.id, guestType.id) !== undefined && (<p className="price-guest-type">
                            {seatType.name}: {getPrice(seatType.id, guestType.id) ? getPrice(seatType.id, guestType.id) + "円" : "無料"}
                          </p>)
                        }</>
                      ))
                    }
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </PriceListModalStyle>
    )
  }, [getPrice, props.guestTypes, props.seatTypes])


  const component = useMemo(() => {
    return (
      <SeatSelectStyle>
        <div className="header">
          <div className="title">座席選択</div>
          <div className="menu">
            <div className="flex-menu">
              <div className="selectedSize">
                {props.totalGroupSize}席中 {props.selectedSeatIndexes.length}席選択中
              </div>
              <div className="button">
                <div>
                  <Button
                    onClick={props.refreshSeats}
                    label="座席状況を更新"
                    buttonType={isMobile() ? "default-powerful" : "powerful-red"}
                  />
                </div>
                <div>
                  { showPriceList && (
                    <Button
                      onClick={() => { setShowPriceModal(true) }}
                      label="価格一覧を表示"
                      buttonType={isMobile() ? "default-powerful" : "powerful-red"}
                    />
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`seat-select ${isMobile() ? "mobile" : ""}`}>
            <SeatsDisplay
              seats={props.seats}
              size="small"
              seatTypes={props.seatTypes}
              onSeatClicked={onSeatClick}
              colorRule={colorRule}
              lockRule={lockRule}
            />
        </div>
        <div className="menu">
          <div className="button">
            <Button
              onClick={props.onSubmit}
              disabled={disableSubmit}
              buttonType={isMobile() ? "default-red" : "powerful-red"}
              label="次へ"
            />
          </div>
          <div className="selected-seats-wrapper">
            <div className="selected-seats">
              {selectedSeatsNames.map((seatName, index) => (
                <div key={"selected-seat-" + index} className="selected-seat">
                  <p>{seatName}</p>
                  <p></p>
                </div>
              ))}
            </div>
          </div>
        </div>
        {snackBar.component}
      </SeatSelectStyle>
    );
  }, [colorRule, disableSubmit, lockRule, onSeatClick, props.onSubmit, props.refreshSeats, props.seatTypes, props.seats, props.selectedSeatIndexes.length, props.totalGroupSize, selectedSeatsNames, showPriceList, snackBar.component]);
  return (
    <>
      {component}
      {showPriceModal && priceListModal}
    </>
  );
};

const SeatSelectStyle = styled.div`
  background-color: rgba(255, 255, 255, 0.6);
  padding: 10px 0 10px 0;
  .header {
    .title {
      margin: auto;
      width: auto;
      text-align: center;
    }
    .menu {
      text-align: center;
      .flex-menu {
        width: 100%;
        margin: 16px auto 16px auto;
        max-height: 120px;
        .selectedSize {
          width: 200px;
          margin: auto;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }
  .seat-select {
    background-color: white;
    color: black;
    max-height: 500px;
    width: 90%;
    margin: 10px auto 10px auto;
    overflow: scroll;
    &.mobile {
      max-height: 600px;
    }
  }
  .menu {
    .button {
      display: flex;
      margin: 8px auto 0 auto;
      text-align: center;
      gap: 8px;
      justify-content: center;
    }
    .selected-seats-wrapper {
      background-color: rgba(255, 255, 255, 0.5);
      width: 90%;
      margin: 10px auto 10px auto;
      .selected-seats {
        display: grid;
        grid-template-columns: 33% 33% 33%;
        justify-content: center;
        .selected-seat {
          width: 100px;
          background-color: black;
          text-align: center;
          margin: 5px auto 5px auto;
        }
      }
    }
  }
`;

const PriceListModalStyle = styled.div`
  .modal-overlay {
    background-color: black;
    opacity: 0.7;
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    z-index: 99998;
  }
  .modal-wrapper {
    z-index: 99999;
    width: 300px;
    left: 50%;
    transform: translate(-50%);
    position: fixed;
    top: 100px;
    .modal {
      margin: auto;
      width: 300px;
      height: 400px;
      background-color: white;
      border-radius: 8px;
      color: black;
      padding: 16px;
      overflow-y: scroll;
      .modal-title {
        margin: auto;
        text-align: center;
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 16px;
      }
      .price-seat-type {
        margin: 16px auto 16px auto;
        .price-seat-type-name {
          font-size: 16px;
          font-weight: 700;
        }
        .price-guest-type {
          margin: 4px auto 4px auto;
          font-size: 16px;
          text-align: center;
        }
      }
    }
  }
`

export default SeatSelectArea;
